import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { useAnalysisCuboidActions } from '@/components/Analysis/modules/cuboids/hooks';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import * as analysisState from '@/components/Analysis/state';
import * as state from '@/components/Analysis/state';
import { useImageFromLocationId } from '@/components/Analysis/Viewer/hooks/useImageFromLocationId';
import { BLISTER_TYPE } from '@/constants';
import { abyssColors } from '@/theme/colors';
import { AnalysisDrawerRight, Point3d, PointOfInterestTemplateDocument } from '@/types';
import { titleCase, toggleValue } from '@/utils';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { useTriggerPoiEditing } from '../../hooks';
import { PointOfInterest, PointOfInterestTemplate } from '@/__generated__/graphql';

export const DetailsOptionMenu = ({ selectedPoiInfo }: { selectedPoiInfo: PointOfInterest }) => {
  const [editPointOfInterest, setEditPointOfInterest] = useAtom(poiState.editPointOfInterest);
  const [drawerRight, setDrawerRight] = useAtom(analysisState.drawerRight);
  const [selectedSpherical, setSpherical] = useAtom(analysisState.selectedSpherical);
  const setBlisterToAdd = useSetAtom(poiState.blisterToAdd);
  const setDrawerRightPreviousState = useSetAtom(analysisState.drawerRightPreviousState);
  const setCameraTarget = useSetAtom(analysisState.cameraTarget);
  const setWidgetTemplate = useSetAtom(poiState.selectedTemplate);
  const allViewpoints = useAtomValue(analysisState.structureLocations);
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorElement);

  const { triggerEdit } = useTriggerPoiEditing();
  const getSphericalFromId = useImageFromLocationId(allViewpoints);
  const { startEditingPoiCuboids } = useAnalysisCuboidActions();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleRightToggle = useCallback(
    (property: keyof AnalysisDrawerRight) => {
      setDrawerRight(toggleValue<AnalysisDrawerRight>(drawerRight, property));
      setDrawerRightPreviousState(property);
    },
    [drawerRight, setDrawerRight, setDrawerRightPreviousState]
  );

  const handleInitPOI = () => {
    handleRightToggle('equipment');
    if (!selectedSpherical && selectedPoiInfo.location?.id) {
      const newSpherical = getSphericalFromId(selectedPoiInfo.location?.id);
      if (newSpherical?.name) {
        const { x, y, z } = selectedPoiInfo.centerPoint3d || {};
        if (!x || !y || !z) return;
        const lookAt: Point3d = { x, y, z };
        setCameraTarget({
          position: newSpherical.position,
          lookAt: [x, y, z],
        });
        if (newSpherical) {
          setSpherical({
            ...newSpherical,
            lookAt,
          });
        }
      }
    }
    const template = allPointOfInterestTemplates.find(
      ({ name }) => name === titleCase(BLISTER_TYPE)
    ) as PointOfInterestTemplateDocument;
    setWidgetTemplate(template);
    setBlisterToAdd({ state: 'WaitingForCenter' });
    setEditPointOfInterest({
      ...editPointOfInterest,
      state: 'WaitingForPoint',
      pointOfInterest: selectedPoiInfo,
      isAutoGenerated: true,
      poiSphericalId: selectedPoiInfo.location?.id,
      template,
    });
  };

  const templateOfSelectedPoi = useMemo(() => {
    return allPointOfInterestTemplates.find(({ id }) => id === selectedPoiInfo.template?.id);
  }, [allPointOfInterestTemplates, selectedPoiInfo]);

  const handleEdit = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (templateOfSelectedPoi) {
      triggerEdit({
        formState: 'Update-Template',
        pointOfInterest: selectedPoiInfo,
        template: templateOfSelectedPoi as PointOfInterestTemplate,
      });
      startEditingPoiCuboids(selectedPoiInfo);
    }
  };

  return (
    <Box>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        disableRipple
        sx={{ padding: 0 }}
      >
        <MoreVertIcon
          sx={{
            margin: 0,
            fontSize: '2.4rem',
            color: abyssColors.primary[500],
          }}
        />
      </IconButton>
      <Menu
        anchorEl={anchorElement}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '.MuiMenuItem-root': {
            minWidth: '15rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.8rem',
          },
        }}
      >
        <MenuItem onClick={handleEdit}>
          <AbyssIcon size="sm" iconName="measure" />
          <span>Edit</span>
        </MenuItem>

        {selectedPoiInfo.isAutoGenerated && (
          <MenuItem onClick={handleInitPOI}>
            <AbyssIcon size="sm" iconName="measure" />
            <span>Remeasure profile</span>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
